/**
 * Design Tokens - View R Studio Design System
 * This file contains all design tokens from the Figma design system
 */

// ============================================
// COLORS
// ============================================

interface ColorValue {
  dark: string;
  light: string;
  1440?: string;
  1024?: string;
}

interface OpacityColor {
  [key: number]: string;
}

export type ViewRBreakpoint = "compact" | "tablet" | "desktop" | "wide";

export const colors = {
  // Grey Scale
  grey: {
    10: {
      dark: "#121212",
      light: "#FAFAFA",
    },
    20: {
      dark: "#1E1E20",
      light: "#F1F1F1",
    },
    30: {
      dark: "#2A2A2D",
      light: "#F1F1F1",
      1440: "#2C2C2E",
      1024: "#2C2C2E",
    },
    40: {
      dark: "#3A3A3E",
      light: "#CDCDCD",
    },
    50: {
      dark: "#5B5B5F",
      light: "#C1C1C1",
    },
    60: {
      dark: "#78787C",
      light: "#9CA3AF",
    },
    70: {
      dark: "#9B9B9F",
      light: "#797F89",
    },
    80: {
      dark: "#BDBDBF",
      light: "#484E56",
    },
    90: {
      dark: "#E5E5E6",
      light: "#0F0F0F",
    },
  } as Record<number, ColorValue>,

  // Common
  common: {
    0: {
      dark: "#000000",
      light: "#FFFFFF",
    },
    100: {
      dark: "#FFFFFF",
      light: "#000000",
    },
  } as Record<number, ColorValue>,

  // Cyan
  cyan: {
    51: {
      dark: "#55F6FF",
      light: "#55F6FF",
    },
  } as Record<number, ColorValue>,

  // Green
  green: {
    50: {
      dark: "#6CFF26",
      light: "#6CFF26",
    },
  } as Record<number, ColorValue>,

  // Primary
  primary: {
    10: {
      dark: "#F6FBF9",
      light: "#F6FBF9",
    },
    20: {
      dark: "#DCF1E7",
      light: "#DCF1E7",
    },
    40: {
      dark: "#28C59A",
      light: "#28C59A",
    },
    50: {
      dark: "#50B786",
      light: "#50B786",
    },
  } as Record<number, ColorValue>,

  // Red
  red: {
    50: {
      dark: "#FF4242",
      light: "#FF4242",
    },
  } as Record<number, ColorValue>,

  // Yellow
  yellow: {
    50: {
      dark: "#FFE435",
      light: "#FFE435",
    },
  } as Record<number, ColorValue>,

  // Brand (Figma Design System)
  brand: {
    viewrBlue: {
      dark: "#2F36E8",
      light: "#2F36E8",
    },
    viewrGreenAqua: {
      dark: "#2FE8CB",
      light: "#2FE8CB",
    },
  } as Record<string, ColorValue>,

  // Grey extended (Figma)
  greyExtended: {
    F2F2F7: {
      dark: "#F2F2F7",
      light: "#F2F2F7",
    },
    EFEFEF: {
      dark: "#EFEFEF",
      light: "#EFEFEF",
    },
    C7C7CC: {
      dark: "#C7C7CC",
      light: "#C7C7CC",
    },
    D1D1D6: {
      dark: "#D1D1D6",
      light: "#D1D1D6",
    },
  } as Record<string, ColorValue>,

  // Accent (Figma palette - semantic use)
  accent: {
    blue: {
      primary: "#2F36E8",
      dark: "#252BC4",
      darker: "#1E239F",
      light: "#5E63EF",
      lighter: "#9A9CFF",
      lightest: "#C8CAFF",
    },
    purple: {
      primary: "#6F36E8",
      dark: "#582CC4",
      darker: "#3E239F",
    },
    greenAqua: {
      primary: "#2FE8CB",
      dark: "#1AC7AF",
      darker: "#00B395",
      darkest: "#00806E",
      light: "#52D1B8",
      lighter: "#87E8D7",
      lightest: "#B8FFE9",
      darkBg: "#006456",
      darkerBg: "#00322B",
    },
    red: {
      primary: "#FF3B30",
      dark: "#D62820",
      darker: "#6B1A16",
      light: "#FF6E66",
      lighter: "#FFB3AF",
      lightest: "#FFE6E5",
    },
    orange: {
      primary: "#FF9500",
      dark: "#D17A00",
      darker: "#6B4000",
      light: "#FFB352",
      lighter: "#FFE6A1",
      lightest: "#FFF2D1",
    },
    yellow: {
      primary: "#E8CB2F",
      dark: "#C4AF25",
      darker: "#9F951E",
    },
  },

  // Opacity Colors
  opacity: {
    black: {
      5: "rgba(0, 0, 0, 0.05)",
      10: "rgba(0, 0, 0, 0.1)",
      20: "rgba(0, 0, 0, 0.2)",
      30: "rgba(0, 0, 0, 0.3)",
      40: "rgba(0, 0, 0, 0.4)",
      50: "rgba(0, 0, 0, 0.5)",
      60: "rgba(0, 0, 0, 0.6)",
      70: "rgba(0, 0, 0, 0.7)",
      80: "rgba(0, 0, 0, 0.8)",
      90: "rgba(0, 0, 0, 0.9)",
      100: "rgba(0, 0, 0, 1)",
    } as OpacityColor,
    grey: {
      30: "rgba(153, 153, 153, 0.3)",
      45: "rgba(51, 51, 51, 0.45)",
    } as OpacityColor,
    white: {
      10: "rgba(255, 255, 255, 0.1)",
    } as OpacityColor,
  },
};

// ============================================
// FILL COLORS (Semantic tokens)
// ============================================

export const fill = {
  background: {
    common0: {
      dark: colors.common[0].dark,
      light: colors.common[0].light,
    },
    common100: {
      dark: colors.common[100].dark,
      light: colors.common[100].light,
    },
    dim: {
      dark: colors.opacity.black[50],
      light: colors.opacity.black[50],
    },
    grey10: {
      dark: colors.grey[10].dark,
      light: colors.grey[10].light,
    },
    grey20: {
      dark: colors.grey[20].dark,
      light: colors.grey[20].light,
    },
    grey30: {
      dark: colors.grey[30].dark,
      light: colors.grey[30].light,
    },
    grey60: {
      dark: colors.grey[60].dark,
      light: colors.grey[60].light,
    },
    grey80: {
      dark: colors.grey[80].dark,
      light: colors.grey[80].light,
    },
    primary40: {
      dark: colors.primary[40].dark,
      light: colors.primary[40].light,
    },
    primary50: {
      dark: colors.primary[50].dark,
      light: colors.primary[50].light,
    },
  },

  icon: {
    common100: {
      dark: colors.common[100].dark,
      light: colors.common[100].light,
    },
    grey10: {
      dark: colors.grey[10].dark,
      light: colors.grey[10].light,
    },
    grey40: {
      dark: colors.grey[40].dark,
      light: colors.grey[40].light,
    },
    grey60: {
      dark: colors.grey[60].dark,
      light: colors.grey[60].light,
    },
    grey80: {
      dark: colors.grey[80].dark,
      light: colors.grey[80].light,
    },
    yellow50: {
      dark: colors.yellow[50].dark,
      light: colors.yellow[50].light,
    },
    primary40: {
      dark: colors.primary[40].dark,
      light: colors.primary[40].light,
    },
    primary50: {
      dark: colors.primary[50].dark,
      light: colors.primary[50].light,
    },
  },

  shape: {
    common0: {
      dark: colors.common[0].dark,
      light: colors.common[0].light,
    },
    common100: {
      dark: colors.common[100].dark,
      light: colors.common[100].light,
    },
    grey10: {
      dark: colors.grey[10].dark,
      light: colors.grey[10].light,
    },
    grey20: {
      dark: colors.grey[20].dark,
      light: colors.grey[20].light,
    },
    grey30: {
      dark: colors.grey[30].dark,
      light: colors.grey[30].light,
    },
    grey40: {
      dark: colors.grey[40].dark,
      light: colors.grey[40].light,
    },
    opacityBlack5: {
      dark: colors.opacity.black[5],
      light: colors.opacity.black[5],
    },
    opacityBlack10: {
      dark: colors.opacity.black[10],
      light: colors.opacity.black[10],
    },
    opacityWhite10: {
      dark: colors.opacity.white[10],
      light: colors.opacity.white[10],
    },
    opacityBlack50: {
      dark: colors.opacity.black[50],
      light: colors.opacity.black[50],
    },
    primary10: {
      dark: colors.primary[10].dark,
      light: colors.primary[10].light,
    },
    primary20: {
      dark: colors.primary[20].dark,
      light: colors.primary[20].light,
    },
    primary50: {
      dark: colors.primary[50].dark,
      light: colors.primary[50].light,
    },
  },

  text: {
    common0: {
      dark: colors.common[0].dark,
      light: colors.common[0].light,
    },
    common100: {
      dark: colors.common[100].dark,
      light: colors.common[100].light,
    },
    grey10: {
      dark: colors.grey[10].dark,
      light: colors.grey[10].light,
    },
    grey40: {
      dark: colors.grey[40].dark,
      light: colors.grey[40].light,
    },
    grey60: {
      dark: colors.grey[60].dark,
      light: colors.grey[60].light,
    },
    grey80: {
      dark: colors.grey[80].dark,
      light: colors.grey[80].light,
    },
    grey90: {
      dark: colors.grey[90].dark,
      light: colors.grey[90].light,
    },
    primary50: {
      dark: colors.primary[50].dark,
      light: colors.primary[50].light,
    },
    error: {
      dark: colors.red[50].dark,
      light: colors.red[50].light,
    },
    success: {
      dark: colors.green[50].dark,
      light: colors.green[50].light,
    },
  },
};

// ============================================
// STROKE COLORS
// ============================================

export const stroke = {
  grey30: {
    dark: colors.grey[30].dark,
    light: colors.grey[30].light,
  },
  grey40: {
    dark: colors.grey[40].dark,
    light: colors.grey[40].light,
  },
  grey50: {
    dark: colors.grey[50].dark,
    light: colors.grey[50].light,
  },
  grey60: {
    dark: colors.grey[60].dark,
    light: colors.grey[60].light,
  },
  grey80: {
    dark: colors.grey[80].dark,
    light: colors.grey[80].light,
  },
  primary50: {
    dark: colors.primary[50].dark,
    light: colors.primary[50].light,
  },
};

// ============================================
// EFFECTS COLORS
// ============================================

export const effects = {
  whiteOpacity10: {
    dark: colors.opacity.white[10],
    light: colors.opacity.white[10],
  },
  blackOpacity5: {
    dark: colors.opacity.black[5],
    light: colors.opacity.black[5],
  },
  blackOpacity10: {
    dark: colors.opacity.black[10],
    light: colors.opacity.black[10],
  },
  blackOpacity20: {
    dark: colors.opacity.black[20],
    light: colors.opacity.black[20],
  },
  blackOpacity30: {
    dark: colors.opacity.black[30],
    light: colors.opacity.black[30],
  },
  blackOpacity40: {
    dark: colors.opacity.black[40],
    light: colors.opacity.black[40],
  },
  blackOpacity50: {
    dark: colors.opacity.black[50],
    light: colors.opacity.black[50],
  },
  blackOpacity60: {
    dark: colors.opacity.black[60],
    light: colors.opacity.black[60],
  },
  blackOpacity70: {
    dark: colors.opacity.black[70],
    light: colors.opacity.black[70],
  },
  blackOpacity80: {
    dark: colors.opacity.black[80],
    light: colors.opacity.black[80],
  },
  blackOpacity90: {
    dark: colors.opacity.black[90],
    light: colors.opacity.black[90],
  },
};

// ============================================
// SHADOWS (Figma spec)
// ============================================

export const shadows = {
  /** FileUploadToast - Figma Globalupload */
  fileUploadToast: "-4px -3px 18px rgba(0, 0, 0, 0.25)",
  /** Sidebar - Figma: 0px 0px 25px rgba(255,255,255,0.1) */
  sidebar: "0px 0px 25px rgba(255, 255, 255, 0.1)",
  /** Modal overlay */
  modal: "0px 16px 48px rgba(0, 0, 0, 0.5)",
  /** Import file modal - Figma shadow/popOver (0,6 / r10) */
  popOver: "0px 6px 10px rgba(0, 0, 0, 0.25)",
  /** Riquid Glass Dark Mode - Figma glass morphism effect for login buttons */
  riquidGlass: [
    "inset 1px 1px 0px -0.5px #333333",
    "inset -1px -1px 0px -0.5px #262626",
    "inset 1px 1px 0.5px -1px #FFFFFF",
    "inset -1px -1px 0.5px -1px #FFFFFF",
    "inset 0px 0px 3px 0px rgba(255, 255, 255, 0.5)",
    "inset 0px 0px 16px 0px rgba(242, 242, 242, 0.06)",
    "0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
    "0px 0px 4px 0px rgba(0, 0, 0, 0.1)",
  ].join(", "),
};

// ============================================
// SEMANTIC TOKENS (Figma UI spec)
// ============================================

export const semantic = {
  /** View R AVP authentication — AVPLogin 15657:148 and Code Sign in 13345:5040.
   * Login initial 708×892 · Code default 9096:29095 · Invalid code 9096:29097. */
  avpAuthentication: {
    login: {
      canvas: {
        width: 708,
        height: {
          email: 892,
          emailError: 920,
          password: 741,
          passwordError: 769,
          create: 857,
          createError: 885,
        },
        radius: "48px",
        background: fill.background.grey20.dark,
        paddingX: 64,
        paddingY: 80,
      },
      content: {
        width: 467,
        containerWidth: 580,
        sectionGap: 52,
        formGap: 20,
        authOptionsGap: 48,
        methodGap: 28,
        guestGap: 32,
      },
      brand: {
        logoSize: 56,
        welcomeSize: 28,
        productSize: 48,
        headerGap: 12,
        gap: 20,
      },
      input: {
        height: 60,
        radius: "20px",
        background: "rgba(0, 0, 0, 0.30)",
        // AVP Text Field / Error (15588:2961), fill/text/red-50.
        errorColor: "#FF453A",
        fontSize: 18,
      },
      button: {
        height: 60,
        radius: "20px",
        fontSize: 20,
        background: fill.shape.primary50.dark,
      },
      social: {
        // AVP StudioSign-in (15657:191, 203–205), fill/shape/grey-30.
        background: "#212121",
        size: 64,
        gap: 16,
      },
      guest: {
        height: 44,
        fontSize: 20,
        fontWeight: 500,
      },
      policy: {
        fontSize: 17,
        lineHeight: "20px",
      },
    },
    access: {
      canvas: {
        width: 640,
        height: 526,
        radius: "46px",
        background: fill.background.grey20.dark,
      },
      navigation: {
        height: 92,
        backButtonSize: 44,
        inset: 24,
      },
      content: {
        width: 467,
        gap: 24,
      },
      row: {
        minHeight: 56,
        labelWidth: 120,
      },
      alert: {
        width: 512,
        radius: "56px",
        background: "rgba(128, 128, 128, 0.30)",
      },
    },
    codeSignIn: {
      canvas: {
        width: 640,
        height: 444,
        radius: "46px",
        sectionGap: 24,
        bottomPadding: 64,
        background: fill.background.grey20.dark,
      },
      navigation: {
        width: 592,
        height: 92,
        backButtonSize: 44,
        backButtonTop: 24,
        titleTop: 30,
        titleHeight: 33,
      },
      content: {
        width: 444,
        height: 264,
        sectionGap: 30,
        instructionsWidth: 364,
        instructionsHeight: 40,
        instructionsToInputGap: 28,
      },
      input: {
        width: 64,
        height: 70,
        radius: "16px",
        gap: 12,
        background: "#212121",
        shadow:
          "inset 0 -0.524px 1.049px rgba(255,255,255,0.3), inset 0 -0.524px 1.049px rgba(255,255,255,0.25), inset 1.049px 1.573px 4.196px rgba(0,0,0,0.08), inset 1.049px 1.573px 4.196px rgba(0,0,0,0.1)",
      },
      feedback: {
        defaultSlotHeight: 26,
        errorGap: 12,
        errorHeight: 16,
        color: "#FF453A",
      },
      button: {
        width: 374,
        height: 60,
        radius: "20px",
        background: fill.shape.primary50.dark,
        selectedOverlay: colors.opacity.white[10],
      },
    },
  },
  /** Mobile aliases. Values preserve the approved dark mobile UI while sharing primitives. */
  mobile: {
    shell: {
      background: fill.background.grey20.dark,
      surface: fill.background.grey30.dark,
      recessedSurface: fill.background.grey10.dark,
      border: stroke.grey40.dark,
      contentMaxWidth: 480,
      pagePaddingX: 20,
    },
    focus: {
      color: fill.shape.primary50.dark,
      outline: `2px solid ${fill.shape.primary50.dark}`,
      outlineOffset: "2px",
    },
    text: {
      primary: fill.text.common100.dark,
      strong: fill.text.grey90.dark,
      secondary: fill.text.grey80.dark,
      muted: fill.text.grey60.dark,
      inverse: fill.text.common0.dark,
      danger: colors.red[50].dark,
    },
    icon: {
      active: fill.icon.primary50.dark,
      primary: fill.icon.common100.dark,
      secondary: fill.icon.grey80.dark,
      muted: fill.icon.grey60.dark,
      inverse: colors.common[0].dark,
    },
    header: {
      background: fill.background.grey20.dark,
      height: 72,
      backHeight: 104,
      backNavigationHeight: 112,
      actionSize: 44,
      search: {
        height: 48,
        background: fill.shape.grey30.dark,
        text: fill.text.grey90.dark,
        icon: fill.icon.grey80.dark,
        radius: "999px",
      },
    },
    navigation: {
      height: 72,
      itemHeight: 52,
      background: colors.opacity.black[50],
      activeBackground: "rgba(255, 255, 255, 0.05)",
      activeBorder: fill.shape.grey30.dark,
      active: fill.text.primary50.dark,
      inactive: fill.text.grey60.dark,
      radius: "999px",
      fadeBackground:
        "linear-gradient(to top, #191919 77.569%, rgba(25, 25, 25, 0) 100%)",
    },
    emptyState: {
      iconBackground: colors.opacity.white[10],
      icon: fill.icon.primary50.dark,
      title: fill.text.common100.dark,
      description: fill.text.grey80.dark,
      iconSize: 40,
      iconRadius: "8px",
    },
    card: {
      background: fill.shape.grey30.dark,
      recessedBackground: fill.background.grey10.dark,
      border: fill.shape.grey30.dark,
      strongBorder: stroke.grey40.dark,
      radius: "12px",
      selectedBackground: "rgba(255, 255, 255, 0.05)",
    },
    sheet: {
      background: fill.shape.grey30.dark,
      border: stroke.grey40.dark,
      backdrop: colors.opacity.black[10],
      handle: colors.grey[60].dark,
      radius: "24px",
      handleRadius: "999px",
    },
    control: {
      background: fill.background.grey10.dark,
      neutralBackground: fill.shape.grey40.dark,
      active: fill.shape.primary50.dark,
      activeAlt: colors.primary[40].dark,
      activeBright: "#1ED995",
      activeTrack: "rgba(80, 183, 134, 0.3)",
      disabled: fill.shape.grey40.dark,
      disabledContent: fill.icon.grey60.dark,
      border: stroke.grey40.dark,
      hoverBorder: stroke.grey50.dark,
      overlay: colors.opacity.black[50],
      overlayShadow: "0 0 8px rgba(0, 0, 0, 0.25)",
    },
    annotation: {
      marker: fill.shape.common100.dark,
      markerText: colors.grey[20].dark,
      selectedDot: fill.shape.primary50.dark,
    },
    viewer: {
      loadingBackground: "#2F2F35",
      loadingGradient:
        "radial-gradient(ellipse 170px 229px at 50% 50%, rgba(47, 47, 54, 0.75) 0%, #2F2F35 100%)",
      topScrim: "linear-gradient(180deg, rgba(0,0,0,0.72), rgba(0,0,0,0))",
    },
  },
  /** Border rendering aliases for 1x displays. Keep layout width with a
   * transparent border and render the visible stroke as a shadow so rounded
   * corners do not lose most of their opacity to antialiasing. */
  border: {
    crispPrimary1: {
      color: stroke.primary50.dark,
      layoutBorder: "1px solid transparent",
      boxShadow: `0 0 0 1px ${stroke.primary50.dark}`,
    },
  },
  /** Button aliases - current production values only */
  button: {
    radius: "8px",
    /** Empty Prototype panel Import action (View R UI 1554:119718). */
    emptyPrototypeImportAction: {
      width: 331,
      maxWidth: "100%",
      minWidth: 100,
      height: 48,
      paddingLeft: "8px",
      paddingRight: "12px",
      paddingY: "10px",
      radius: "8px",
      background: "transparent",
      hoverBackground: fill.shape.opacityBlack10.dark,
      border: `1px solid ${stroke.primary50.dark}`,
      shadow: "none",
      text: fill.text.primary50.dark,
      iconSize: 24,
      gap: "0px",
    },
    primaryAction: {
      background: {
        default: colors.primary[50],
        hover: colors.primary[40],
      },
      text: {
        dark: "#FFFFFF",
        light: "#000000",
      },
      hoverShadow: "0 6px 18px rgba(0,0,0,0.22)",
    },
    /** Login-flow PrimaryButton states from View-R Design System.
     * Default 159:22465 · Hovered 159:22470 · Selected 159:22476. */
    authPrimaryAction: {
      minWidth: 100,
      height: 48,
      paddingX: "12px",
      paddingY: "10px",
      radius: "8px",
      background: colors.primary[50],
      hoverOverlay: colors.opacity.black[10],
      selectedOverlay: colors.opacity.white[10],
      text: "#FFFFFF",
      fontSize: 16,
      lineHeight: "20px",
      fontWeight: 400,
    },
    secondaryAction: {
      background: "secondary.main",
      text: "#0D0F13",
      hoverBackground: "secondary.dark",
    },
    subtleAction: {
      background: "transparent",
      text: "text.primary",
      borderColor: "divider",
      hoverBackground: "action.hover",
      hoverBorderColor: "divider",
    },
    containedNeutral: {
      background: {
        default: {
          dark: "#2A2E37",
          light: "#E5E7EB",
        },
        hover: {
          dark: "#343A46",
          light: "#D1D5DB",
        },
        active: {
          dark: "#3B4150",
          light: "#BFC5CE",
        },
      },
      text: {
        dark: "#E5E7EB",
        light: "#111827",
      },
    },
    outlinedNeutral: {
      text: {
        dark: "#D1D5DB",
        light: "#374151",
      },
      border: {
        dark: "#3F4654",
        light: "#D1D5DB",
      },
      hoverBorder: {
        dark: "#4A5262",
        light: "#C7CFDB",
      },
      hoverBackgroundAlpha: {
        dark: 0.04,
        light: 0.03,
      },
    },
    textNeutral: {
      text: {
        dark: "#D1D5DB",
        light: "#4B5563",
      },
      hoverBackgroundAlpha: {
        dark: 0.04,
        light: 0.03,
      },
    },
    iconButton: {
      radius: "8px",
      color: "text.secondary",
      hoverColor: "text.primary",
      hoverBackground: "action.hover",
    },
  },
  /** View R Design system Dropdown (node 13173:607). */
  dropdown: {
    sourceFilter: {
      width: 183,
      height: 40,
      radius: "8px",
      background: fill.background.grey20.dark,
      text: fill.text.common100.dark,
      icon: fill.icon.common100.dark,
      iconSize: 24,
      border: {
        default: stroke.grey40.dark,
        hover: stroke.grey60.dark,
        focused: stroke.grey60.dark,
      },
    },
    contextualMenu: {
      width: 183,
      background: fill.shape.grey30.dark,
      border: `0.5px solid ${stroke.grey30.dark}`,
      radius: "16px",
      shadow: `0 0 12.5px ${effects.blackOpacity30.dark}`,
      paddingY: "8px",
      item: {
        height: 40,
        paddingX: "16px",
        paddingY: "12px",
        gap: "8px",
        text: fill.text.grey90.dark,
        hoverBackground: colors.opacity.white[10],
      },
    },
  },
  /** Input/SearchField aliases - current production values only */
  input: {
    searchField: {
      dashboardTopbar: {
        container: {
          height: 48,
          background: fill.shape.grey30.dark,
          radius: "999px",
          gap: "8px",
          paddingLeft: "20px",
          paddingRight: 0,
        },
        icon: {
          size: 24,
          color: fill.icon.grey80.dark,
        },
        input: {
          color: fill.text.grey80.dark,
          placeholderColor: fill.text.grey80.dark,
          placeholderOpacity: 1,
        },
      },
      compactOutlined: {
        height: 36,
        radius: "8px",
        background: fill.background.grey20.dark,
        text: fill.text.common100.dark,
        placeholder: fill.text.grey60.dark,
        placeholderOpacity: 1,
        icon: {
          size: 16,
          color: fill.text.grey60.dark,
        },
        border: {
          default: stroke.grey40.dark,
          hover: stroke.grey60.dark,
          focused: stroke.grey60.dark,
        },
      },
    },
  },
  /** Dashboard layout aliases - current production values only */
  dashboard: {
    topbar: {
      root: {
        paddingX: "32px",
        paddingY: "32px",
        gap: "16px",
        compactPaddingX: "12px",
        compactPaddingY: "24px",
      },
      searchGroup: {
        gap: "32px",
        compactGap: "12px",
      },
      searchFieldLayout: {
        width: "100%",
        minWidth: 400,
        maxWidth: 600,
        flex: "1 1 600px",
        compactWidth: "100%",
        compactMinWidth: 0,
        compactMaxWidth: "none",
        compactFlex: "0 0 auto",
      },
      actions: {
        gap: "16px",
        compactGap: "8px",
      },
      actionButton: {
        sort: {
          minWidth: 100,
          height: "auto",
          paddingLeft: "8px",
          paddingRight: "4px",
          paddingY: "8px",
          radius: "8px",
          text: fill.text.grey60.dark,
          icon: fill.icon.grey60.dark,
          iconSize: 16,
          hoverBackground: colors.opacity.white[10],
          endIconMarginLeft: 0,
        },
        menu: {
          zIndex: 1000,
          radius: "12px",
          background: fill.shape.grey30.dark,
          itemHoverBackground: colors.opacity.white[10],
          itemText: fill.text.grey90.dark,
        },
        import: {
          background: "transparent",
          text: fill.text.primary50.dark,
          border: fill.shape.primary50.dark,
          borderWidth: 1,
          height: 48,
          minWidth: 100,
          paddingLeft: "8px",
          paddingRight: "12px",
          paddingY: "10px",
          radius: "8px",
          gap: "4px",
          icon: fill.icon.primary50.dark,
          iconSize: 20,
          hoverBackground: colors.opacity.white[10],
          startIconMarginLeft: 0,
          startIconMarginRight: "4px",
        },
        create: {
          background: fill.shape.primary50.dark,
          hoverBackground: fill.background.primary40.dark,
          disabledBackground: colors.grey[50].dark,
          text: fill.text.common100.dark,
          height: 48,
          minWidth: 100,
          paddingLeft: "8px",
          paddingRight: "12px",
          paddingY: "10px",
          radius: "8px",
          gap: "0px",
          iconSize: 24,
          progressSize: 20,
          loadingIconMarginRight: "4px",
          iconMarginRight: 0,
          startIconMarginLeft: 0,
        },
      },
    },
    loginRequiredDialog: {
      surface: {
        width: 500,
        minHeight: 230,
        radius: "16px",
        background: fill.background.grey20.dark,
        text: fill.text.grey90.dark,
        overflow: "hidden",
        backdrop: fill.background.dim.dark,
      },
      spacer: {
        height: 16,
      },
      header: {
        height: 64,
        paddingX: "32px",
        paddingY: 0,
        gap: "8px",
      },
      body: {
        minHeight: 64,
        paddingX: "32px",
        paddingY: 0,
        text: fill.text.grey90.dark,
        messageGap: "4px",
      },
      actions: {
        height: 64,
        paddingX: "32px",
        paddingY: 0,
        gap: "24px",
      },
      closeButton: {
        size: 24,
        // MUI Close 잉크는 viewBox의 14/24이라 12를 주면 X가 7px로 그려진다.
        iconSize: 24,
        color: fill.icon.grey60.dark,
        hoverBackground: colors.opacity.white[10],
      },
      actionButton: {
        secondary: {
          minWidth: 100,
          height: 48,
          paddingLeft: "8px",
          paddingRight: "12px",
          paddingY: "10px",
          radius: "8px",
          border: fill.shape.primary50.dark,
          text: fill.text.primary50.dark,
          hoverBackground: colors.opacity.white[10],
        },
        primary: {
          minWidth: 100,
          height: 48,
          paddingLeft: "8px",
          paddingRight: "12px",
          paddingY: "10px",
          radius: "8px",
          background: fill.shape.primary50.dark,
          hoverBackground: fill.background.primary40.dark,
          text: fill.text.common100.dark,
        },
      },
    },
    guestLoginDialog: {
      surface: {
        background: fill.shape.grey20.dark,
        radius: "24px",
        padding: "56px",
        gap: "64px",
        width: "auto",
        height: "auto",
        maxWidth: "none",
      },
      navButton: {
        size: 24,
        padding: 0,
        color: fill.icon.grey80.dark,
        hoverBackground: colors.opacity.white[10],
        iconSize: 15,
      },
      title: {
        text: fill.text.grey90.dark,
        lineHeight: 0,
      },
      legal: {
        width: 336,
        lineHeight: 0,
        text: fill.text.grey80.dark,
        linkHoverOpacity: 0.8,
      },
    },
    saveChangedConfirmDialog: {
      surface: {
        width: 500,
        height: 230,
        minWidth: 500,
        minHeight: 230,
        radius: "16px",
        background: fill.background.grey20.dark,
        text: fill.text.grey90.dark,
        overflow: "hidden",
        backdrop: fill.background.dim.dark,
      },
      spacer: {
        height: 16,
      },
      header: {
        height: 64,
        paddingX: "32px",
        gap: "8px",
      },
      body: {
        minHeight: 64,
        paddingX: "32px",
        text: fill.text.grey90.dark,
      },
      actions: {
        height: 64,
        paddingX: "32px",
        gap: "24px",
      },
      closeButton: {
        size: 24,
        // MUI Close 잉크는 viewBox의 14/24이라 12를 주면 X가 7px로 그려진다.
        iconSize: 24,
        color: fill.icon.grey60.dark,
        disabledOpacity: 0.4,
        hoverBackground: colors.opacity.white[10],
        hoverDisabledBackground: "transparent",
        hoverRadius: "4px",
      },
      actionButton: {
        secondary: {
          minWidth: 100,
          height: 48,
          paddingLeft: "8px",
          paddingRight: "12px",
          radius: "8px",
          border: stroke.primary50.dark,
          text: fill.text.primary50.dark,
          hoverBackground: colors.opacity.white[10],
        },
        primary: {
          minWidth: 100,
          height: 48,
          paddingLeft: "8px",
          paddingRight: "12px",
          radius: "8px",
          background: fill.icon.primary50.dark,
          hoverBackground: fill.icon.primary50.dark,
          text: fill.text.common100.dark,
          spinnerSize: 16,
          spinnerThickness: 5,
          spinnerGap: 1,
        },
      },
    },
    trashFileCard: {
      card: {
        // Figma "Home Screen Template"(159:25565) Thumbnail 폭
        width: 370,
        height: 284,
        warningHeight: 310,
        radius: "16px",
        backgroundClip: "padding-box",
        background: "transparent",
        border: `1px solid ${stroke.grey40.dark}`,
        hoverBorder: `2px solid ${stroke.primary50.dark}`,
        shadow: "none",
        transition: "border 0.2s ease, transform 0.05s ease",
      },
      media: {
        height: 212,
        background: "transparent",
        transform: "translateZ(0)",
      },
      content: {
        height: 72,
        // 자동 삭제 안내가 한 줄 늘어난 카드(310)의 정보 영역. 카드 높이만 늘리고
        // 이 값을 72로 두면 안내 줄이 영역 밖으로 밀리고 카드 아래가 비어 보인다.
        warningHeight: 98,
        paddingX: "16px",
        titleWeight: 500,
        autoDeleteText: "#ff9800",
        autoDeleteMarginTop: 0.5,
      },
      menuButton: {
        right: 0,
        top: "50%",
        transform: "translateY(-50%)",
      },
      deleteDialog: {
        titleWeight: 600,
        actionsPaddingX: 3,
        actionsPaddingBottom: 2,
        cancelText: "#28C59A",
      },
    },
    rawFilesListSection: {
      root: {
        paddingY: 0,
      },
      actionError: {
        paddingX: 2,
        paddingY: 1,
        fontSize: 12,
      },
      bulkDeleteDialog: {
        surface: {
          width: 500,
          radius: "16px",
          background: fill.background.grey20.dark,
          text: fill.text.common100.dark,
        },
        title: {
          gap: 1,
          paddingRight: 6,
        },
        warningIcon: {
          color: colors.accent.yellow.primary,
          size: 20,
        },
        closeButton: {
          right: 12,
          top: 12,
          color: fill.icon.grey60.dark,
        },
        bodyText: {
          color: fill.text.grey90.dark,
        },
        actions: {
          paddingX: 3,
          paddingBottom: 2,
        },
        destructiveAction: {
          border: colors.red[50].dark,
          text: colors.red[50].dark,
          background: "rgba(255, 66, 66, 0.1)",
          textTransform: "none",
        },
      },
    },
  },
  /** Library detail drawer aliases. */
  libraryDetailDrawer: {
    /** View R UI Tooltip (3168:105371). */
    storageTooltip: {
      background: fill.shape.common0.dark,
      text: fill.text.common100.dark,
      radius: "8px",
      paddingLeft: "12px",
      paddingRight: "8px",
      paddingY: "12px",
      maxWidth: "none",
      arrow: fill.shape.common0.dark,
    },
    /** View R Design System ConversionCheck (14982:6496). */
    conversionCheck: {
      successSummary: "#99999E",
      rowLabel: "#EDEDF0",
      detail: "#6B6B70",
      warning: "#FFB020",
      notCheckedValue: "#B8B8BF",
      chip: {
        warningBackground: "rgba(255, 176, 32, 0.14)",
        notCheckedBackground: "rgba(122, 122, 128, 0.14)",
        border: "1px solid rgba(255, 176, 32, 0.34)",
        text: fill.text.common100.dark,
        radius: "20px",
        paddingX: "8px",
        paddingY: "3px",
      },
    },
    /** View R UI Object-panel overflow menu (3175:105380). */
    overflowMenu: {
      width: 247,
      background: fill.background.grey20.dark,
      border: `0.5px solid ${stroke.grey30.dark}`,
      radius: "16px",
      shadow: "0 6px 10px rgba(0, 0, 0, 0.45)",
      paddingY: "8px",
      item: {
        height: 40,
        outerPaddingX: "8px",
        innerPaddingX: "8px",
        radius: "8px",
        text: fill.text.grey90.dark,
        destructiveText: colors.red[50].dark,
        hoverBackground: colors.opacity.white[10],
      },
    },
    footerAction: {
      normal: {
        openMinWidth: 120,
        closeMinWidth: 80,
        height: 40,
        radius: "8px",
        border: stroke.grey60.dark,
        text: fill.text.grey80.dark,
        hoverBackground: colors.opacity.white[10],
      },
      destructive: {
        height: 48,
        radius: "8px",
        border: "#E53935",
        background: "rgba(229, 57, 53, 0.1)",
        text: "#E53935",
        hoverBackground: "rgba(229, 57, 53, 0.15)",
        fontWeight: 500,
      },
    },
  },
  /** Figma PAT settings dialog aliases - current production values only */
  figmaPatSettingsDialog: {
    action: {
      disconnect: {
        minWidth: 100,
        height: 48,
        paddingX: "12px",
        paddingY: "10px",
        radius: "8px",
        background: "rgba(255, 0, 0, 0.1)",
        border: "1px solid #ff4242",
        text: "#ff4242",
        hoverBackground: "rgba(255, 0, 0, 0.16)",
      },
      externalLink: {
        height: 48,
        border: `1px solid ${fill.icon.common100.dark}`,
        radius: "8px",
        text: fill.icon.common100.dark,
        hoverBackground: "rgba(255, 255, 255, 0.16)",
        iconSize: 16,
      },
      verifyIcon: {
        size: 28,
        background: fill.shape.opacityWhite10.dark,
        text: fill.icon.common100.dark,
        hoverBackground: "rgba(255, 255, 255, 0.16)",
        iconSize: 16,
        spinnerSize: 16,
      },
    },
  },
  /** Editor Figma/ProtoPie list row.
   * View R Design System: Figma Row Content 14277:2 / LibraryRowContent 14263:2486. */
  editorPrototypeList: {
    row: {
      preferredWidth: 339,
      height: 71,
      bleed: "8px",
      padding: "8px",
      gap: "8px",
      radius: "8px",
      hoverBackground: fill.shape.opacityBlack10.dark,
      selectedBackground: fill.shape.grey40.dark,
    },
    thumbnail: {
      width: 99,
      height: 55,
      background: fill.shape.grey10.dark,
    },
    metadata: {
      height: 55,
      gap: "8px",
      titleHeight: 15,
      titleInset: "4px",
      titleWidth: 180,
      actionSize: 24,
      sourceSlotSize: 17,
      figmaIconWidth: 6.641,
      figmaIconHeight: 9.969,
      protoPieIconSize: 14,
      statusHeight: 14,
      statusGap: "4px",
      statusDotSize: 7,
      text: fill.text.grey60.dark,
      connected: fill.text.success.dark,
      error: fill.text.error.dark,
    },
  },
  /** Account deletion dialogs - current production values only */
  accountDeletionDialog: {
    action: {
      normal: {
        minWidth: 100,
        height: 48,
        paddingLeft: "8px",
        paddingRight: "12px",
        paddingY: "10px",
        radius: "8px",
        border: stroke.grey60.dark,
        text: fill.text.grey80.dark,
        hoverBackground: colors.opacity.white[10],
      },
      destructive: {
        minWidth: 100,
        height: 48,
        paddingLeft: "8px",
        paddingRight: "12px",
        paddingY: "10px",
        radius: "8px",
        border: "#FF4242",
        background: "rgba(255, 0, 0, 0.1)",
        text: "#FF4242",
        hoverBackground: "rgba(255, 0, 0, 0.15)",
      },
    },
  },
  /** AI panel aliases - current editor-adjacent production values only */
  aiPanel: {
    cta: {
      height: "32px",
      paddingX: "12px",
      paddingY: "6px",
      minWidth: "64px",
      radius: "8px",
      background: "#8B5CF6",
      hoverBackground: "#7C3AED",
      activeBackground: "#6D28D9",
      disabledBackground: "#3A3A3E",
      text: "#FFFFFF",
      disabledText: "#78787C",
      iconGap: "4px",
      iconSize: "16px",
      contentPaddingX: "16px",
    },
  },
  /** Toast aliases - current production values only */
  toast: {
    default: {
      position: {
        verticalOffset: "32px",
        horizontalOffset: "20px",
        centerLeft: "50%",
        centerTransform: "translateX(-50%)",
      },
      stack: {
        maxVisible: 2,
        step: 72,
      },
      surface: {
        background: fill.shape.opacityBlack50.dark,
        radius: "8px",
        paddingX: "16px",
        paddingY: "14px",
        minHeight: 56,
        gap: "8px",
        width: "min(calc(100vw - 40px), 477px)",
        maxWidth: 477,
        // Figma Toast "No icon"(Measurement 13 · Outside Model Toast 5878:149530): 내용 폭에 맞추고 최소 320px.
        noIconMinWidth: 320,
        shadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
      },
      icon: {
        size: 24,
      },
      text: {
        color: fill.text.common100.dark,
        lineHeight: "20px",
      },
      action: {
        color: fill.text.primary50.dark,
        // 비활성 action. 이 코드베이스가 비활성·inactive 텍스트에 쓰는 토큰을 그대로 참조한다.
        disabledColor: fill.text.grey60.dark,
        paddingX: "12px",
        paddingY: "4px",
        radius: "4px",
        hoverBackground: fill.shape.opacityWhite10.dark,
        focusOutlineWidth: "2px",
        focusOutlineOffset: "2px",
      },
      closeButton: {
        padding: 0,
        minSize: 24,
        color: fill.icon.common100.dark,
        hoverBackground: fill.shape.opacityWhite10.dark,
        iconSize: 20,
      },
    },
    uploadPanel: {
      width: 331,
      headerHeight: 48,
      rowHeight: 82,
      background: "#2A2A2D",
      text: colors.common[100].dark,
      headerText: colors.grey[80].dark,
      statusGrey: "#BDBDBF",
      statusFailed: "#E21919",
      statusAttention: "#FFE435",
      progressTrack: "rgba(255, 255, 255, 0.10)",
      progressGrey: "#78787C",
      progressFailedTrack: "#3A3A3E",
      topRadius: "12px",
      shadow: shadows.fileUploadToast,
      headerIcon: {
        color: colors.grey[80].dark,
        size: "24px",
        radius: "999px",
      },
      cancelIcon: {
        color: colors.red[50].dark,
        size: "24px",
        radius: "50%",
      },
    },
    downloadPanel: {
      width: 331,
      expandedHeight: 130,
      minimizedHeight: 48,
      rowHeight: 82,
      background: "#2A2A2D",
      text: colors.common[100].dark,
      headerText: colors.grey[80].dark,
      failedText: colors.red[50].dark,
      progressTrack: "rgba(255, 255, 255, 0.10)",
      progressComplete: colors.primary[50].dark,
      progressFailed: colors.red[50].dark,
      topRadius: "12px",
      shadow: shadows.fileUploadToast,
      headerIcon: {
        color: colors.grey[80].dark,
        size: "24px",
        radius: "999px",
      },
      cancelIcon: {
        color: colors.red[50].dark,
        size: "24px",
        radius: "50%",
      },
    },
  },
  /** Dialog/Modal aliases - current production values only */
  dialog: {
    base: {
      width: {
        sm: "min(420px, calc(100vw - 32px))",
        md: "min(500px, calc(100vw - 32px))",
        lg: "min(626px, calc(100vw - 64px))",
      },
      maxWidth: "100%",
      maxHeight: "min(90vh, 720px)",
      tallMaxHeight: "min(90vh, 880px)",
      background: fill.background.grey20.dark,
      text: fill.text.common100.dark,
      radius: "16px",
      overflow: "hidden",
      shadow: shadows.modal,
      backdrop: fill.background.dim.dark,
    },
    layout: {
      topSpacer: 16,
      bottomSpacer: 16,
      headerHeight: 64,
      bodyMinHeight: 64,
      actionsHeight: 64,
      horizontalPadding: "32px",
      headerGap: "8px",
      actionsGap: "24px",
    },
    typography: {
      titleColor: fill.text.grey90.dark,
      bodyColor: fill.text.grey90.dark,
      secondaryTextColor: fill.text.grey60.dark,
    },
    closeButton: {
      size: 24,
      // MUI Close 글리프는 24 viewBox 안에서 잉크가 14만 차지한다. 12를 주면
      // X가 7px로 그려져 24 박스 안에서 절반 크기로 보인다. 24가 Figma의
      // 잉크 14px과 같다.
      iconSize: 24,
      color: fill.icon.grey60.dark,
      hoverColor: fill.text.grey90.dark,
      hoverBackground: "transparent",
      hoverRadius: "4px",
    },
    // Header Container(node 14569:2036)의 뒤로가기. close와 같은 24 박스지만
    // 아이콘 취급이 다르다. Figma의 `Navi Left arrow`는 24 프레임 자체가 아이콘이고
    // chevron(8x15)이 그 프레임 왼쪽 끝에 붙어 있어, 프레임을 줄이면 타이틀과의
    // 간격 8px이 어긋난다. 그래서 iconSize가 size와 같다.
    backButton: {
      size: 24,
      iconSize: 24,
      color: fill.icon.grey60.dark,
      hoverColor: fill.text.grey90.dark,
      hoverBackground: "transparent",
      hoverRadius: "4px",
    },
    destructive: {
      width: 500,
      compactHeight: 230,
      minSpaceDeleteHeight: 269,
      dangerText: colors.red[50].dark,
      dangerBackground: "rgba(255, 0, 0, 0.1)",
      dangerHoverBackground: "rgba(255, 0, 0, 0.15)",
      warningIcon: colors.accent.yellow.primary,
    },
    capacityExceeded: {
      width: 500,
      fileWarningBackground: "rgba(255,66,66,0.05)",
      fileWarningBorder: "1px solid rgba(255,66,66,0.2)",
      convertedSizeText: "#ff4242",
    },
    figmaImport: {
      width: 626,
      compactWidth: 500,
      maxWidth: "calc(100vw - 64px)",
    },
  },
  /** Measurement / Axis Value Chip: View R UI 5883:100734, 6672:6358. */
  measurement: {
    chipBackground: "rgba(128, 128, 128, 0.3)",
    chipBlur: "40px",
    axisPaddingLeft: "6px",
    axis: { x: "#FF4242", y: "#50B786", z: "#4A90E2" },
  },
  /** Conversion report failure modal (View R UI 2903:98902). */
  conversionReport: {
    width: "min(560px, calc(100vw - 32px))",
    bodyGap: "20px",
    /** Warning report card / value chip (View R UI 4146:76692/76693). */
    warning: {
      cardRadius: "10px",
      chipBackground: "rgba(255, 176, 32, 0.16)",
    },
    headerIcon: "/assets/ConversionReport/Error.svg",
    statusChip: {
      failedBackground: "rgba(255, 66, 66, 0.14)",
      failedBorder: "rgba(255, 66, 66, 0.34)",
      warningBackground: "rgba(255, 228, 53, 0.14)",
      warningBorder: "rgba(255, 228, 53, 0.34)",
    },
    step: {
      // 미도달 라벨·번호는 grey 60, rail은 grey 40 (View R UI 4133:76651/76652).
      pending: "#78787C",
      railPending: "#3A3A3E",
      reason: "#8C8C94",
      // Marker 24 box / rail 1px / Content offset 36 (View R UI 4133:76628).
      dotSize: 24,
      railWidth: 1,
      rowGap: "16px",
      columnGap: "12px",
      reasonGap: "10px",
    },
  },
  /** Tooltip aliases - current production values only */
  tooltip: {
    muiDefault: {
      placement: "top",
      arrow: true,
    },
    gizmo: {
      background: "#000",
      text: fill.text.common100.dark,
      radius: "8px",
      gap: "4px",
      paddingLeft: "12px",
      paddingRight: "8px",
      paddingY: "12px",
      maxWidth: 426,
      arrow: {
        borderLeft: "5px solid transparent",
        borderRight: "5px solid transparent",
        borderBottom: "6px solid #000",
      },
      closeButton: {
        color: fill.text.common100.dark,
        hoverBackground: "rgba(255,255,255,0.1)",
        iconSize: 20,
      },
    },
    floatingUpload: {
      zIndex: 10_000,
    },
  },
  /** Loading aliases - current production values only */
  loading: {
    centerSpinner: {
      radius: 35,
      thickness: 10,
      ariaLabel: "Loading",
      speedSec: 1.4,
      color: colors.primary[50].dark,
      trackColor: "rgba(80, 183, 134, 0.3)",
      arcRatio: 0.2,
      position: {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      },
    },
    circular: {
      small: 16,
      medium: 24,
      modal: 28,
      large: 32,
      buttonThickness: 5,
      primaryColor: colors.primary[50].dark,
      inheritColor: "inherit",
    },
    progress: {
      uploadDownload: {
        height: 8,
        radius: "40px",
        track: "rgba(255, 255, 255, 0.10)",
        fill: colors.primary[50].dark,
        failedFill: colors.red[50].dark,
      },
      exportPanel: {
        height: 4,
        radius: "4px",
        track: fill.shape.grey40.dark,
        fill: fill.shape.common100.dark,
      },
      storageUsage: {
        height: 9,
        containerRadius: "13px",
        fillRadius: "20px",
        track: fill.background.grey80.dark,
        fill: fill.shape.primary50.dark,
      },
      spaceTransition: {
        radius: 35,
        thickness: 10,
        track: "rgba(34, 90, 62, 0.85)",
        fill: colors.primary[50].dark,
        text: "#ffffff",
      },
    },
  },
  /** Library row selection - primary green 8% */
  libraryRowSelectedBg: "rgba(80, 183, 134, 0.08)",
  /** Library table aliases - current production values only */
  libraryTable: {
    color: {
      border: colors.grey[40].dark,
      hoverBg: colors.opacity.white[10],
    },
    selected: {
      background: "rgba(80, 183, 134, 0.08)",
      hoverBackground: "rgba(80, 183, 134, 0.08)",
      borderLeft: stroke.primary50.dark,
    },
    typography: {
      headerName: {
        fontFamily: "'Pretendard', sans-serif",
        fontWeight: 400,
        fontSize: 13,
        lineHeight: "15px",
      },
      headerMeta: {
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "14px",
      },
      rowName: {
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: 14,
        lineHeight: "16px",
      },
      rowMeta: {
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "14px",
      },
    },
  },
  /** Delete button outline style - Figma Delete permanently Modal */
  deleteButtonOutline: {
    bg: "rgba(255, 66, 66, 0.1)",
    border: colors.red[50].dark,
    text: colors.red[50].dark,
  },
  /** Stop processing modal - Figma 6956:70459 / 6956:70458 */
  stopProcessingModal: {
    width: 500,
    height: 356,
    barHeight: 16,
    headerHeight: 64,
    descriptionHeight: 64,
    processHeight: 132,
    buttonRowHeight: 64,
    horizontalPadding: 32,
    processHorizontalPadding: 16,
    processTopPadding: 12,
    processBottomPadding: 28,
    buttonGap: 24,
    continueButtonWidth: 171,
    stopButtonWidth: 140,
    stepItemWidth: 154,
    stepGap: 3,
    stepIconSize: 40,
    fileNameMaxChars: 12,
  },
  /** Modal dimensions - Figma */
  modalWidth: 500,
};

// ============================================
// TYPOGRAPHY
// ============================================

interface TextStyle {
  fontSize: string;
  fontWeight: number;
  lineHeight: string;
  letterSpacing: string;
  fontFamily: string;
}

export const typography = {
  fontSize: {
    10: "10px",
    12: "12px",
    13: "13px",
    14: "14px",
    15: "15px",
    16: "16px",
    17: "17px",
    18: "18px",
    19: "19px",
    20: "20px",
    21: "21px",
    23: "23px",
    24: "24px",
    28: "28px",
    32: "32px",
    36: "36px",
    48: "48px",
    50: "50px",
    56: "56px",
    64: "64px",
    80: "80px",
    100: "100px",
  } as Record<number, string>,

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 500, // Updated to match Figma (was 600)
    bold: 700,
    black: 900,
  },

  lineHeight: {
    10: "10px",
    14: "14px",
    15: "15px",
    16: "16px",
    19: "19px",
    20: "20px",
    21: "21px",
    22: "22px",
    23: "23px",
    28: "28px",
    32: "32px",
    38: "38px",
    42: "42px",
    50: "50px",
    56: "56px",
    64: "64px",
    66: "66px",
    80: "80px",
    100: "100px",
  } as Record<number, string>,

  letterSpacing: {
    0: "0px",
  } as Record<number, string>,

  // Font families
  fontFamily: {
    default: "Roboto, sans-serif",
    roboto: "Roboto, sans-serif",
    pretendard:
      "Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
  },

  // Pre-defined text styles based on Figma design system
  styles: {
    // Display styles
    display1: {
      fontSize: "56px",
      fontWeight: 500,
      lineHeight: "66px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    display2: {
      fontSize: "48px",
      fontWeight: 400,
      lineHeight: "56px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // ViewR Studio Brand Styles (from Figma)
    viewrStudio: {
      display1Semibold: {
        fontSize: "56px",
        fontWeight: 500,
        lineHeight: "66px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      display2Regular: {
        fontSize: "48px",
        fontWeight: 400,
        lineHeight: "56px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      titleMedium: {
        fontSize: "24px",
        fontWeight: 500,
        lineHeight: "28px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      headingMedium: {
        fontSize: "20px",
        fontWeight: 500,
        lineHeight: "23px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      headingRegular: {
        fontSize: "20px",
        fontWeight: 400,
        lineHeight: "23px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      headline1Regular: {
        fontSize: "18px",
        fontWeight: 400,
        lineHeight: "21px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      headline1Medium: {
        fontSize: "18px",
        fontWeight: 500,
        lineHeight: "21px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      headline2Regular: {
        fontSize: "17px",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "0px",
        fontFamily:
          "Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
      } as TextStyle,
      headline2Medium: {
        fontSize: "17px",
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      bodyRegular: {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      label1Medium: {
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "16px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      label2Regular: {
        fontSize: "13px",
        fontWeight: 400,
        lineHeight: "15px",
        letterSpacing: "0px",
        fontFamily:
          "Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
      } as TextStyle,
      label2Medium: {
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "15px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
      captionRegular: {
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "14px",
        letterSpacing: "0px",
        fontFamily: "Roboto, sans-serif",
      } as TextStyle,
    },

    // Title
    title: {
      fontSize: "24px",
      fontWeight: 500,
      lineHeight: "28px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // Heading styles
    heading: {
      fontSize: "20px",
      fontWeight: 500,
      lineHeight: "23px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    headingRegular: {
      fontSize: "20px",
      fontWeight: 400,
      lineHeight: "23px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // Headline styles
    headline1: {
      fontSize: "18px",
      fontWeight: 400,
      lineHeight: "21px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    headline1Medium: {
      fontSize: "18px",
      fontWeight: 500,
      lineHeight: "21px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    headline2: {
      fontSize: "17px",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    headline2Medium: {
      fontSize: "17px",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    headline3: {
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // Body
    body: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    body1: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    body2: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // Label styles
    label1: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "16px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    label1Medium: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "16px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    label2: {
      fontSize: "13px",
      fontWeight: 400,
      lineHeight: "15px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
    label2Medium: {
      fontSize: "13px",
      fontWeight: 500,
      lineHeight: "15px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,

    // Caption
    caption: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "14px",
      letterSpacing: "0px",
      fontFamily: "Roboto, sans-serif",
    } as TextStyle,
  },
};

// ============================================
// ICONS
// ============================================

export const icons = {
  // 16x16 Icons
  size16: {
    // Social Login
    socialAppleLight: "/assets/Icons16/Social Logina_apple_light.svg",
    socialAppleDark: "/assets/Icons16/Social Login_apple_dark.svg",

    // Media Controls
    stop: "/assets/Icons16/Stop.svg",
    pause: "/assets/Icons16/Pause.svg",
    play: "/assets/Icons16/Property 1=Play.svg",

    // Actions
    add: "/assets/Icons16/Property 1=Add.svg",
    trash: "/assets/Icons16/Property 1=Trash.svg",
    rename: "/assets/Icons16/Property 1=Rename.svg",
    clear: "/assets/Icons16/Property 1=Clear.svg",
    check: "/assets/Icons16/Property 1=Check.svg",
    close: "/assets/Icons16/Property 1=Close.svg",

    // Navigation
    rightArrow: "/assets/Icons16/Property 1=Right arrow.svg",
    leftArrow: "/assets/Icons16/Property 1=Left arrow.svg",
    downArrow: "/assets/Icons16/Property 1=Downarrow.svg",
    enter: "/assets/Icons16/Property 1=Enter.svg",
    fold: "/assets/Icons16/Property 1=Fold.svg",

    // UI Elements
    settings: "/assets/Icons16/Property 1=Settings.svg",
    logout: "/assets/Icons16/Property 1=Logout.svg",
    minimize: "/assets/Icons16/Property 1=Minimize.svg",
    information: "/assets/Icons16/Property 1=Information.svg",
    gridView: "/assets/Icons16/Property 1=Grid View.svg",
    home: "/assets/Icons16/Property 1=Home.svg",
    file: "/assets/Icons16/Property 1=File.svg",
    clock: "/assets/Icons16/Property 1=Clock.svg",

    // Guide
    guide: "/assets/Icons16/Property 1=16 Guide.svg",

    // Sidebar SVG Icons (React components in assets/SVGs/SvgIcons.tsx)
    svgRecents: "assets/SVGs/Icon 16_Recents.svg",
    svgTrash: "assets/SVGs/Icon 16_trash.svg",
    svgLibrary: "assets/SVGs/Icon 16.svg",

    // Import file modal Icons16 (assets/Icons16/*.svg)
    svgLibraryFile: "/assets/Icons16/library.svg",
    svgSearch: "/assets/Icons16/search.svg",
    svgUpload: "/assets/Icons16/upload.svg",
  } as Record<string, string>,

  // 24x24 Icons
  size24: {
    // User & Profile
    user: "/assets/Icons24/Property 1=User.svg",
    profile: "/assets/Icons24/Property 1=Profile.svg",

    // Media Controls
    play: "/assets/Icons24/Property 1=_Play.svg",
    loading: "/assets/Icons24/Property 1=_Loading.svg",

    // Actions
    add: "/assets/Icons24/Property 1=Add.svg",
    trash: "/assets/Icons24/Property 1=Trash.svg",
    rename: "/assets/Icons24/Property 1=Rename.svg",
    save: "/assets/Icons24/Property 1=Save.svg",
    download: "/assets/Icons24/Property 1=Download.svg",
    clear: "/assets/Icons24/Property 1=Clear.svg",
    check: "/assets/Icons24/Property 1=Check.svg",
    close: "/assets/Icons24/Property 1=Close.svg",
    complete: "/assets/Icons24/Property 1=Complete.svg",

    // Navigation
    upArrow: "/assets/Icons24/Property 1=Up arrow.svg",
    downArrow: "/assets/Icons24/Property 1=Down arrow.svg",
    downArrowSolid: "/assets/Icons24/Property 1=DownArrow Solid.svg",
    naviLeftArrow: "/assets/Icons24/Property 1=Navi Left arrow.svg",
    arrow: "/assets/Icons24/Property 1=Arrow.svg",
    enter: "/assets/Icons24/Property 1=Enter.svg",
    fold: "/assets/Icons24/Property 1=Fold.svg",

    // UI Elements
    setting: "/assets/Icons24/Property 1=Setting.svg",
    logout: "/assets/Icons24/Property 1=Logout.svg",
    minimize: "/assets/Icons24/Property 1=Minimize.svg",
    gridView: "/assets/Icons24/Property 1=Grid View.svg",
    grid: "/assets/Icons24/Property 1=Grid.svg",
    home: "/assets/Icons24/Property 1=Home.svg",
    homeSolid: "/assets/Icons24/Property 1=Home Solid.svg",
    list: "/assets/Icons24/Property 1=List.svg",
    miniView: "/assets/Icons24/Property 1=Mini view.svg",
    tooltip: "/assets/Icons24/Property 1=Tooltip.svg",

    // Content Types
    folder: "/assets/Icons24/Property 1=Folder.svg",
    note: "/assets/Icons24/Property 1=Note.svg",
    media: "/assets/Icons24/Property 1=Media.svg",
    slide: "/assets/Icons24/Property 1=Slide.svg",
    drawing: "/assets/Icons24/Property 1=Drawing.svg",
    library: "/assets/Icons24/Property 1=Library.svg",

    // Tools
    search: "/assets/Icons24/Property 1=Search.svg",
    eye: "/assets/Icons24/Property 1=Eye.svg",
    lock: "/assets/Icons24/Property 1=Lock.svg",
    pin: "/assets/Icons24/Property 1=Pin.svg",
    thumbnailCamera: "/assets/Icons24/Property 1=Thumbnail Camera.svg",
    dropper: "/assets/Icons24/Property 1=Dropper.svg",
    map: "/assets/Icons24/Property 1=Map.svg",
    ai: "/assets/Icons24/Property 1=AI.svg",

    // Status & Feedback
    error: "/assets/Icons24/Property 1=Error.svg",
    clock: "/assets/Icons24/Property 1=Clock Color.svg",
    alarm: "/assets/Icons24/Property 1=Alarm.svg",
    notice: "/assets/Icons24/Property 1=Notice.svg",

    // Checkboxes & Selection (Icon 25: check circle for Choose from library)
    svgCheckCircle: "/assets/Icons24/Icon 25.svg",
    checkbox: "/assets/Icons24/Property 1=Checkbox.svg",
    checkboxEmpty: "/assets/Icons24/Property 1=Checkbox Empty.svg",
    bookmarkOn: "/assets/Icons24/Property 1=Bookmark On.svg",
    bookmarkOff: "/assets/Icons24/Property 1=Bookmark Off.svg",

    // Special
    entrypoint: "/assets/Icons24/Property 1=Entrypoint.svg",
    /** XR Entry Point 버튼 (Bottom Toolbar) - VR 헤드셋 아이콘 */
    svgXrEntryPoint: "/assets/SVGs/Icon 24.svg",
    addPrototype: "/assets/Icons24/Property 1=Import Figma/ProtoPie.svg",

    // Guide
    guide: "/assets/Icons24/Property 1=24Guide.svg",

    // SVG Icons (React components in assets/SVGs/SvgIcons.tsx)
    svgCloud: "assets/SVGs/cloud_FILL1_wght400_GRAD0_opsz48 1.svg",
  } as Record<string, string>,

  // Unity object toolbar icons (custom size, usually ~28px)
  unityToolbar: {
    move: "/assets/Unity_Icons/Toolbar_Move.png",
    angle: "/assets/Unity_Icons/Toolbar_Angle.png",
    ruler: "/assets/Unity_Icons/Toolbar_Ruler.png",
    parts: "/assets/Unity_Icons/Toolbar Parts.png",
  } as Record<string, string>,
};

// Helper function to get icon path by size and name
export const getIcon = (size: number, name: string): string | null => {
  const sizeKey = size === 16 ? "size16" : "size24";
  return (icons[sizeKey] as Record<string, string>)?.[name] || null;
};

// ============================================
// BUTTONS (Component Specs)
// ============================================

export const buttons = {
  // Button heights
  height: {
    small: "32px",
    medium: "40px",
    large: "48px",
  },

  // Button padding (horizontal, vertical)
  padding: {
    small: {
      px: "12px",
      py: "6px",
    },
    medium: {
      px: "16px",
      py: "8px",
    },
    large: {
      px: "24px",
      py: "12px",
    },
  },

  // Icon sizes for buttons
  iconSize: {
    small: "16px",
    medium: "20px",
    large: "24px",
  },

  // Gap between icon and text
  iconGap: {
    small: "4px",
    medium: "8px",
  },

  // Min width for buttons
  minWidth: {
    small: "64px",
    medium: "80px",
    large: "120px",
  },

  // Border radius
  borderRadius: {
    small: "8px",
    medium: "8px",
    large: "12px",
  },

  // Button colors
  colors: {
    primary: {
      background: {
        default: "#8B5CF6",
        hover: "#7C3AED",
        active: "#6D28D9",
        disabled: {
          dark: "#3A3A3E",
          light: "#CDCDCD",
        },
      },
      text: {
        default: {
          dark: "#FFFFFF",
          light: "#FFFFFF",
        },
        disabled: {
          dark: "#78787C",
          light: "#9CA3AF",
        },
      },
    },
    secondary: {
      background: {
        default: {
          dark: "#2A2A2D",
          light: "#F1F1F1",
        },
        hover: {
          dark: "#3A3A3E",
          light: "#E5E5E6",
        },
        active: {
          dark: "#5B5B5F",
          light: "#CDCDCD",
        },
      },
      text: {
        default: {
          dark: "#FFFFFF",
          light: "#000000",
        },
      },
    },
  },
};

// Helper function to get button styles
export const getButtonStyles = (
  size: "small" | "medium" | "large" = "medium",
  variant: "primary" | "secondary" = "primary",
  mode: "dark" | "light" = "dark",
): Record<string, unknown> => {
  const sizeStyles = {
    height: buttons.height[size],
    px: buttons.padding[size].px,
    py: buttons.padding[size].py,
    minWidth: buttons.minWidth[size],
    borderRadius: buttons.borderRadius[size],
  };

  const colorStyles = {
    background:
      variant === "primary"
        ? buttons.colors.primary.background.default
        : buttons.colors.secondary.background.default[mode],
    color:
      variant === "primary"
        ? buttons.colors.primary.text.default[mode]
        : buttons.colors.secondary.text.default[mode],
    "&:hover": {
      background:
        variant === "primary"
          ? buttons.colors.primary.background.hover
          : buttons.colors.secondary.background.hover[mode],
    },
    "&:active": {
      background:
        variant === "primary"
          ? buttons.colors.primary.background.active
          : buttons.colors.secondary.background.active[mode],
    },
    "&:disabled": {
      background:
        variant === "primary"
          ? buttons.colors.primary.background.disabled[mode]
          : buttons.colors.secondary.background.hover[mode],
      color: buttons.colors.primary.text.disabled[mode],
    },
  };

  return { ...sizeStyles, ...colorStyles };
};

// ============================================
// NUMBERS (Spacing, Sizing, etc.)
// ============================================

export const numbers: Record<string | number, unknown> = {
  // Base numbers
  0: 0,
  1: 1,
  2: 2,
  4: 4,
  8: 8,
  10: 10,
  12: 12,
  16: 16,
  20: 20,
  24: 24,
  32: 32,
  40: 40,
  48: 48,
  56: 56,
  64: 64,
  80: 80,
  100: 100,
  120: 120,
  140: 140,
  160: 160,
  180: 180,
  200: 200,
  250: 250,
  300: 300,
  350: 350,
  400: 400,
  450: 450,
  500: 500,
  600: 600,
  700: 700,
  750: 750,
  768: 768,
  800: 800,
  900: 900,
  999: 999,
  1000: 1000,
  1024: 1024,
  1080: 1080,
  1200: 1200,
  1440: 1440,
  1600: 1600,
  1920: 1920,

  // Corner Radius
  cornerRadius: {
    0: "0px",
    1: "1px",
    2: "2px",
    4: "4px",
    6: "6px",
    8: "8px",
    12: "12px",
    16: "16px",
    24: "24px",
    32: "32px",
    40: "40px",
    80: "80px",
    90: "90px",
    100: "100px",
    200: "200px",
    500: "500px",
    999: "999px", // Full rounded
  } as Record<number, string>,

  // Effects (blur, etc.)
  effects: {
    4: "4px",
    16: "16px",
    32: "32px",
  } as Record<number, string>,

  // Gap (spacing)
  gap: {
    0: "0px",
    4: "4px",
    7: "7px",
    8: "8px",
    10: "10px",
    12: "12px",
    16: "16px",
    24: "24px",
    32: "32px",
    48: "48px",
    56: "56px",
    64: "64px",
    80: "80px",
    120: "120px",
    140: "140px",
  } as Record<number, string>,

  // Layer opacity
  layerOpacity: {
    0: 0,
    10: 0.1,
    20: 0.2,
    30: 0.3,
    40: 0.4,
    50: 0.5,
    60: 0.6,
    70: 0.7,
    80: 0.8,
    90: 0.9,
    100: 1,
  } as Record<number, number>,

  // Stroke width
  stroke: {
    1: "1px",
    2: "2px",
  } as Record<number, string>,

  // Width & Height
  widthHeight: {
    16: "16px",
    32: "32px",
    48: "48px",
    56: "56px",
    64: "64px",
    80: "80px",
    100: "100px",
    200: "200px",
    300: "300px",
    450: "450px",
    500: "500px",
    700: "700px",
    800: "800px",
    1000: "1000px",
    1024: "1024px",
    1080: "1080px",
    1200: "1200px",
    1440: "1440px",
    1600: "1600px",
    1920: "1920px",
    modalHeight: 0, // Define based on your needs
    modalWidth: 0, // Define based on your needs
  } as Record<string, string | number>,
};

// ============================================
// RESPONSIVE LAYOUT CONTRACTS
// ============================================

/**
 * Responsive constraints for translating fixed Figma frames into production UI.
 *
 * Figma dimensions are treated as preferred desktop sizes. Components should
 * combine these tokens with viewport constraints instead of copying fixed
 * `width`, `height`, `minWidth`, or `minHeight` values directly.
 */
export const responsive = {
  breakpoints: {
    compact: 0,
    tablet: 768,
    desktop: 1024,
    wide: 1440,
  } satisfies Record<ViewRBreakpoint, number>,

  qaViewportWidths: [390, 768, 1440, 1920],

  viewport: {
    /** Below this width, Studio editor should collapse or gate dense editing UI. */
    editorMinimumWidth: 768,
    /** Preferred width for the full editor chrome around the Unity canvas. */
    editorPreferredWidth: 1024,
  },

  spacing: {
    pageGutter: {
      compact: "16px",
      tablet: "24px",
      desktop: "32px",
      wide: "40px",
    } satisfies Record<ViewRBreakpoint, string>,
    modalMargin: {
      compact: "16px",
      tablet: "24px",
      desktop: "32px",
      wide: "32px",
    } satisfies Record<ViewRBreakpoint, string>,
  },

  constraints: {
    modal: {
      sm: "min(420px, calc(100vw - 32px))",
      md: "min(500px, calc(100vw - 32px))",
      lg: "min(626px, calc(100vw - 64px))",
      maxHeight: "min(90vh, 720px)",
      tallMaxHeight: "min(90vh, 880px)",
    },
    panel: {
      sm: "min(320px, calc(100vw - 32px))",
      md: "min(420px, calc(100vw - 32px))",
      lg: "min(520px, calc(100vw - 64px))",
      maxHeight: "calc(100vh - 48px)",
    },
    sidebar: {
      min: 280,
      default: 371,
      max: 480,
      viewportMaxRatio: 0.9,
    },
    card: {
      min: "min(280px, 100%)",
      preferred: "371px",
      gridTemplate: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
    },
    table: {
      desktopMinWidth: "1024px",
      compactScrollMinWidth: "720px",
    },
    toolbar: {
      compactMinHeight: "48px",
      desktopMinHeight: "56px",
      overflow: "horizontal-scroll-or-menu",
    },
  },
} as const;

// ============================================
// LAYER Z-INDEX (stacking contexts)
// ============================================

/** `GlobalUploadManager` 고정 토스트 및 그 위에 그려야 하는 포털 Tooltip */
export const layerZIndex = {
  globalUploadFloating: 9999,
  globalUploadTooltip: 10_000,
} as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get color based on theme mode (dark/light)
 * @param colorObj - Color object with dark and light properties
 * @param mode - 'dark' or 'light'
 * @returns Color value
 */
export const getColorByMode = (
  colorObj: ColorValue,
  mode: "dark" | "light" = "dark",
): string => {
  return colorObj[mode] || colorObj.dark;
};

/**
 * Create MUI theme colors
 * @param mode - 'dark' or 'light'
 * @returns MUI theme color configuration
 */
export const createThemeColors = (
  mode: "dark" | "light" = "dark",
): Record<string, unknown> => ({
  mode,
  primary: {
    main: getColorByMode(colors.primary[50], mode),
    light: getColorByMode(colors.primary[40], mode),
    dark: getColorByMode(colors.primary[50], mode),
  },
  secondary: {
    main: getColorByMode(colors.grey[50], mode),
  },
  error: {
    main: getColorByMode(colors.red[50], mode),
  },
  background: {
    default: getColorByMode(fill.background.grey20, mode),
    paper: getColorByMode(fill.background.grey30, mode),
  },
  text: {
    primary: getColorByMode(fill.text.grey90, mode),
    secondary: getColorByMode(fill.text.grey80, mode),
  },
});

const designTokens = {
  colors,
  fill,
  stroke,
  effects,
  shadows,
  semantic,
  typography,
  icons,
  buttons,
  numbers,
  responsive,
  layerZIndex,
  getColorByMode,
  getIcon,
  getButtonStyles,
  createThemeColors,
};

// ViewR Studio Brand Styles - Easy access
export const viewrStudioStyles = typography.styles.viewrStudio;

export default designTokens;
